import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Card, CardBody, CardFooter, Col,
  DropdownMenu, DropdownToggle, Row, UncontrolledButtonDropdown,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import { getTradeRulesAcceptedForProperty } from 'src/components/TradeRule/helpers';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { emptyTradeRulesClassName } from 'src/helpers/tradeHelpers';
import {
  COMMUNITY, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_COMMUNITY,
} from 'src/util/constants';
import isActive from 'src/util/isActive';

import TradeRule from './TradeRule';

const CommunityTradeRulesFragment = graphql`
  fragment CommunityTradeRules_property on Property {
    uuid
    meters {
      edges {
        node {
          uuid
          identifier
          title
          tradePointId
          active { start finish }
          communityRules: rules(first: 500, type: TRADE_TYPE_COMMUNITY, state: TRADE_RULE_STATE_ACCEPTED) {
            edges {
              node {
                uuid
                priority
                tradeType
                state
                buyer {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                seller {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                clauses {
                  edges {
                    node {
                      price
                      uuid
                      timezone
                      ignoreDaylightSavings
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start  { hours minutes seconds }
                        finish { hours minutes seconds }
                      }
                    }
                  }
                }
                start
                finish
                proposedAt
                proposedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                acceptedAt
                acceptedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * CommunityTrades displays active community trade rules for a property, showing both buying and
 * selling trade rules in a card-based layout. This component is part of the TradeRuleActive
 * interface and provides users with an overview of their community energy trading activities,
 * including approved trade rules, pricing information, and actions to set new community trade
 * rules.
 * @param {object} props - Component props
 * @param {object} props.property - Property containing meters and their associated community
 * trade rules.
 * @returns {React.ReactElement} A Card component containing community trade information.
 */
function CommunityTradeRules(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  const intl = useIntl();

  let { property } = props;
  if (!property) {
    return <Loading />;
  }
  property = useFragment(CommunityTradeRulesFragment, property);

  const { uuid: propertyId } = property || {};
  const tradeRules = getTradeRulesAcceptedForProperty(property, TRADE_TYPE_COMMUNITY);
  const noTradeRulesClass = emptyTradeRulesClassName(tradeRules, COMMUNITY);

  // i18n variables
  const communityTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.title', defaultMessage: 'Community trades' });
  const buyingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.buy.title', defaultMessage: 'Buying' });

  const sellingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.sell.title', defaultMessage: 'Selling' });
  const setCommunityTradesLabel = intl.formatMessage({ id: 'trade_rule_active.community_trade_rules.set_community_trades.label', defaultMessage: 'Set community trade rules' });

  const singleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.trade.singular', defaultMessage: 'community trade rule' });
  const multipleTrades = intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.trade.multiple', defaultMessage: 'community trade rules' });

  return (
    <Card className={`mt-4 mb-4 trade-rules-community${noTradeRulesClass}`}>
      <CardBody>
        <h2>{communityTradesTitle}</h2>
        <p>
          <FormattedMessage
            id="trade_rule.trade_rule_active.community_trade_rules.help_text"
            defaultMessage="Community trades allow you to buy and sell anonymously with other members of your community. The price paid for energy is based on the available supply and the sell and buy price of everyone else in the community."
            values={{
              link: (chunks) => (
                <LinkInI18nBlock
                  url={intl.formatMessage({ id: 'trade_rule.trade_rule_active.community_trade_rules.trade_rule_active_community.help_text.url', defaultMessage: 'trade_rule.trade_rule_active.community_trade_rules.trade_rule_active_community.help_text.url' })}
                  urlKey="trade_rule.trade_rule_active.community_trade_rules.trade_rule_active_community.help_text.url"
                  chunks={chunks}
                />
              ),
            }}
          />
        </p>
        <Row>
          <Col xs="12" sm="12" md="6">
            <h3>{buyingTitle}</h3>
            <p>
              {intl.formatMessage(
                {
                  id: 'trade_rule.trade_rule_active.community_trade_rules.buy.count_details',
                  defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to buy energy.',
                },
                {
                  tradeRulesCount: <strong>{tradeRules.buy.length}</strong>,
                  tradeRulesCountLabel: <FormattedPlural
                    value={tradeRules.buy.length}
                    one={singleTrade}
                    other={multipleTrades}
                  />,
                },
              )}
            </p>

            {tradeRules?.buy?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_BUY}
                tradeType={TRADE_TYPE_COMMUNITY}
                propertyId={propertyId}
                key={rule.uuid}
              />
            ))}
          </Col>
          <Col xs="12" sm="12" md="6">
            <h3>{sellingTitle}</h3>
            {intl.formatMessage(
              {
                id: 'trade_rule.trade_rule_active.community_trade_rules.sell.count_details',
                defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to sell energy.',
              },
              {
                tradeRulesCount: <strong>{tradeRules.sell.length}</strong>,
                tradeRulesCountLabel: <FormattedPlural
                  value={tradeRules.sell.length}
                  one={singleTrade}
                  other={multipleTrades}
                />,
              },
            )}

            {tradeRules?.sell?.map((rule) => (
              <TradeRule
                rule={rule}
                tradeDirection={TRADE_DIRECTION_SELL}
                tradeType={TRADE_TYPE_COMMUNITY}
                propertyId={propertyId}
                key={rule.uuid}
              />
            ))}
          </Col>
        </Row>
      </CardBody>
      <CardFooter>
        <UncontrolledButtonDropdown className="me-2">
          <DropdownToggle caret color="primary">
            {setCommunityTradesLabel}
          </DropdownToggle>
          <DropdownMenu>
            {property.meters.edges.map((edge) => edge && edge.node && (
            <Link
              to={`/properties/${property.uuid}/meters/${edge.node.uuid}/trade-rules/community/set`}
              className={`dropdown-item ${!isActive(edge.node.active) ? 'disabled' : ''}`}
              role="menuitem"
              key={edge.node.uuid}
            >
              {edge.node.title}
            </Link>
            ))}
          </DropdownMenu>
        </UncontrolledButtonDropdown>
      </CardFooter>
    </Card>
  );
}

CommunityTradeRules.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

CommunityTradeRules.defaultProps = {
  property: null,
};

export default CommunityTradeRules;
